import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useAuth } from '../context/AuthContext';
import NeuralNexusBackground from '../components/shared/NeuralNexusBackground';
import { DESIGN_SYSTEM } from '../theme/designSystem';

const functions = getFunctions();
const createCheckoutSession = httpsCallable(functions, 'createCheckoutSession');

// Stripe publishable key — loaded from .env (REACT_APP_STRIPE_PUBLISHABLE_KEY)
const STRIPE_PK = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '';

// ─── STRIPE PRICE IDs ────────────────────────────────────────────────────────
// After creating your Stripe account:
//   1. Go to stripe.com/dashboard → Products → Add Product
//   2. Create each product as a one-time payment
//   3. Copy the Price ID (starts with price_) for each and paste below
// ─────────────────────────────────────────────────────────────────────────────
const PRICES = {
  foundations: {
    priceId: 'price_1TFqtfBN9lZUkcLpBOCsKQ0E',
    tier: 'foundations',
    name: 'Foundations',
    price: '$39',
    modules: 'Modules 0 + 1',
    description: 'The prep course for Full Operator. Closes the exact gaps Operator builds on — so when you step up, nothing slows you down.',
    features: [
      'Module 0 — The Crew (4 lessons)',
      'Module 1 — The Brief (5 lessons)',
      'Interactive before/after exercises',
      'Lesson recap quizzes',
      'Lifetime access*',
    ],
    color: '#4f8ef7',
    cta: 'Build the Foundation',
    tooltipWho: "Your assessment identified gaps in the core fundamentals Full Operator builds on. This course closes those gaps specifically — how AI processes instructions, what makes a brief complete, and how to build a repeatable system. It's prep, not starter content.",
    tooltipWillKnow: 'How AI actually processes instructions, how to write a complete brief that produces reliable output, and the core principles that separate effective operators from everyone else. Everything Full Operator assumes you already know.',
  },
  operator: {
    priceId: 'price_1TFqupBN9lZUkcLpyvzQURwX',
    tier: 'operator',
    name: 'Full Operator',
    price: '$97',
    modules: 'All 5 modules',
    description: 'The complete course — every module, every tool, every workflow method.',
    features: [
      'All 25 lessons across 5 modules',
      'Module 0 through Module 4',
      'Interactive AI sandbox exercises',
      'All lesson recap quizzes',
      'Module tests',
      'Lifetime access*',
    ],
    color: '#7c3aed',
    cta: 'Get Full Operator',
    tooltipWho: 'You use AI regularly and get results — but you hit a ceiling when tasks get complex or multi-step. The assessment confirmed you have the fundamentals. Now it\'s time to stop improvising and build a real system.',
    tooltipWillKnow: 'How to direct AI through complex, dependent task chains, build systems for your most demanding work, and operate confidently across every major use case.',
  },
  certified: {
    priceId: 'price_1TFqwGBN9lZUkcLpV2Tv64Yr',
    tier: 'certified',
    name: 'Certified Operator',
    price: '$149',
    modules: 'All 5 modules + Certification',
    description: 'Go beyond execution — understand how AI actually reasons, where it fails, and how to build systems that account for both.',
    features: [
      'How AI models reason — mechanism, not metaphor',
      'Systematic failure modes + how to engineer around them',
      'Multi-model workflow architecture',
      'Strategic AI integration for complex, high-stakes work',
      'Final 15-question certification exam',
      'Shareable credential + LinkedIn-ready badge',
      'Lifetime access*',
    ],
    color: '#f59e0b',
    cta: 'Get Certified',
    tooltipWho: 'Professionals who want to go beyond executing tasks and understand AI at a reasoning level. Team leads, consultants, and high performers who need to architect AI-powered systems, lead others in using it effectively, and demonstrate verified mastery — not just competence.',
    tooltipWillKnow: 'How AI models actually reason, where they fail and why, and how to engineer around their limits. How to architect multi-step, multi-model workflows that chain outputs into reliable systems. How to operate strategically — not just completing tasks but designing how AI integrates into complex, high-stakes work. Plus a shareable Certified Operator credential that proves you operate at this level.',
  },
};

export default function PricingPage() {
  const { currentUser, userProfile } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(null); // which tier is loading
  const [error, setError] = useState('');
  const [hoveredTier, setHoveredTier] = useState(null);

  async function handleSelect(tier) {
    if (!currentUser) {
      navigate('/signup?returnTo=/pricing', {
        state: { backgroundLocation: location },
      });
      return;
    }

    // Beta users (betaTester flag OR legacy 'beta' tier) get free Stripe enrollment
    // even if a tier was previously granted manually — this creates the Stripe record.
    const isBeta = !!(userProfile?.betaTester || userProfile?.tier === 'beta');

    // Non-beta users who already have this tier or higher — send to chat directly.
    // Beta users always go through Stripe checkout (even at $0) to create a payment record.
    if (!isBeta && (userProfile?.tier === tier || userProfile?.tier === 'certified')) {
      navigate('/v2-course');
      return;
    }

    const hasUnlockedTier = ['foundations', 'operator', 'certified'].includes(userProfile?.tier);
    const checkoutTier    = isBeta ? (hasUnlockedTier ? tier : 'foundations') : tier;
    const checkoutPriceId = PRICES[checkoutTier].priceId;
    const betaCode        = isBeta ? 'beta-tester' : undefined;

    setLoading(tier);
    setError('');
    try {
      const payload = { priceId: checkoutPriceId, tier: checkoutTier };
      if (betaCode) payload.betaCode = betaCode;
      const result = await createCheckoutSession(payload);
      // Redirect to Stripe Checkout (free for beta, paid for others)
      window.location.href = result.data.url;
    } catch (err) {
      const detail = err?.message || err?.code || '';
      setError(`Could not start checkout. ${detail ? '(' + detail + ')' : 'Please try again or contact support.'}`);
      console.error('Checkout error:', err);
    } finally {
      setLoading(null);
    }
  }

  const currentTier = userProfile?.tier;
  const recommendedTier = userProfile?.savedDemo?.recommendedTier || null;
  // Computed at component scope so JSX can also access it
  const isBetaUser = !!(userProfile?.betaTester || userProfile?.tier === 'beta');

  return (
    <div style={styles.page}>
      <NeuralNexusBackground />
      {/* Header */}
      <div style={styles.header}>
        <button onClick={() => navigate(-1)} style={styles.backBtn}>← Back</button>
        <div style={styles.brand}>The Operator Method</div>
        <div style={styles.headline}>Choose your tier</div>
        <div style={styles.subline}>
          One-time payment. Lifetime access. No subscriptions.
        </div>
        {currentUser && (
          <button
            onClick={() => navigate('/dashboard')}
            style={{ marginTop: '12px', background: 'none', border: '1px solid #1e2a3a', borderRadius: '6px', color: '#4f8ef7', cursor: 'pointer', fontSize: '13px', padding: '6px 16px' }}
          >
            Already have access? → Go to Dashboard
          </button>
        )}
      </div>

      {/* Current tier banner */}
      {currentTier && currentTier !== 'beta' && (
        <div style={styles.currentBanner}>
          You currently have <strong style={{ color: '#34d399' }}>{PRICES[currentTier]?.name}</strong> access.
          {currentTier !== 'certified' && ' Upgrade anytime to unlock more.'}
        </div>
      )}

      {/* Beta user banner — shown for betaTester users or legacy beta tier */}
      {currentUser && (userProfile?.betaTester || userProfile?.tier === 'beta') && !['foundations','operator','certified'].includes(userProfile?.tier) && (
        <div style={{
          backgroundColor: 'rgba(52,211,153,0.08)',
          border: '1px solid rgba(52,211,153,0.3)',
          borderRadius: '10px',
          padding: '14px 24px',
          margin: '0 auto 24px',
          maxWidth: '760px',
          textAlign: 'center',
          fontSize: '14px',
          color: '#34d399',
          fontWeight: 600,
        }}>
          ✓ Beta Access Verified — First enrollment starts at Foundations at no cost. You can unlock Operator and Certified later at no cost.
        </div>
      )}

      {/* Assessment recommendation banner */}
      {recommendedTier && !currentTier && (
        <div style={{
          backgroundColor: 'rgba(245,158,11,0.08)',
          border: '1px solid rgba(245,158,11,0.3)',
          borderRadius: '10px',
          padding: '14px 24px',
          margin: '0 auto 24px',
          maxWidth: '760px',
          textAlign: 'center',
          fontSize: '14px',
          color: '#f59e0b',
          fontWeight: 500,
        }}>
          Based on your assessment, we recommend <strong>{PRICES[recommendedTier]?.name}</strong> — it's highlighted below.
        </div>
      )}

      {/* Error — fixed toast so it's always visible */}
      {error && (
        <div style={styles.errorToast}>
          <span>{error}</span>
          <button onClick={() => setError('')} style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', fontSize: '18px', lineHeight: 1, padding: '0 0 0 12px' }}>×</button>
        </div>
      )}

      {/* Tier cards */}
      <div style={styles.grid}>
        {Object.values(PRICES).map((tier) => {
          // Beta users: never treat as owned — they still click through to Stripe ($0 checkout)
          const wouldBeOwned = currentTier === tier.tier ||
            (currentTier === 'certified') ||
            (currentTier === 'operator' && tier.tier === 'foundations');
          const isOwned = !isBetaUser && wouldBeOwned;
          const isHovered = hoveredTier === tier.tier;
          const isRecommended = !isOwned && tier.tier === recommendedTier;

          return (
            <div
              key={tier.tier}
              style={{
                ...styles.card,
                borderColor: isRecommended ? '#f59e0b' : isHovered ? tier.color : 'rgba(255,255,255,0.08)',
                boxShadow: isRecommended ? '0 0 0 1px rgba(245,158,11,0.3), 0 8px 32px rgba(245,158,11,0.08)' : undefined,
              }}
              onMouseEnter={() => setHoveredTier(tier.tier)}
              onMouseLeave={() => setHoveredTier(null)}
            >
              {/* Recommended badge */}
              {isRecommended && (
                <div style={{
                  position: 'absolute', top: '-1px', left: '50%', transform: 'translateX(-50%)',
                  backgroundColor: '#f59e0b', color: '#000',
                  fontSize: '10px', fontWeight: 700, letterSpacing: '0.1em',
                  padding: '3px 12px', borderRadius: '0 0 8px 8px',
                  whiteSpace: 'nowrap',
                }}>
                  ★ RECOMMENDED FOR YOU
                </div>
              )}
              {/* Hover tooltip overlay */}
              {isHovered && (
                <div style={{ ...styles.tooltip, borderColor: tier.color }}>
                  <div style={styles.tooltipSection}>
                    <div style={{ ...styles.tooltipLabel, color: tier.color }}>WHO THIS IS FOR</div>
                    <p style={styles.tooltipText}>{tier.tooltipWho}</p>
                  </div>
                  <div style={styles.tooltipDivider} />
                  <div style={styles.tooltipSection}>
                    <div style={{ ...styles.tooltipLabel, color: tier.color }}>WHAT YOU'LL KNOW</div>
                    <p style={styles.tooltipText}>{tier.tooltipWillKnow}</p>
                  </div>
                  <button
                    onClick={() => handleSelect(tier.tier)}
                    disabled={!!loading || isOwned}
                    style={{
                      ...styles.ctaBtn,
                      background: isOwned ? 'rgba(255,255,255,0.05)' : 'transparent',
                      border: `1px solid ${isOwned ? 'rgba(255,255,255,0.08)' : tier.color}`,
                      color: isOwned ? '#475569' : tier.color,
                      cursor: isOwned ? 'default' : loading ? 'wait' : 'pointer',
                      marginTop: '4px',
                    }}
                  >
                    {isOwned
                      ? '✓ Owned'
                      : isBetaUser && wouldBeOwned
                        ? 'Enroll Free →'
                        : loading === tier.tier
                          ? 'Redirecting...'
                          : tier.cta}
                  </button>
                </div>
              )}

              <div style={{ ...styles.tierColor, background: tier.color }} />
              <div style={styles.tierName}>{tier.name}</div>
              <div style={{ ...styles.tierPrice, color: tier.color }}>{tier.price}</div>
              <div style={styles.tierModules}>{tier.modules}</div>
              <p style={styles.tierDesc}>{tier.description}</p>

              <ul style={styles.featureList}>
                {tier.features.map((f, i) => (
                  <li key={i} style={styles.featureItem}>
                    <span style={{ ...styles.check, color: tier.color }}>✓</span>
                    {f}
                  </li>
                ))}
              </ul>

              <button
                onClick={() => handleSelect(tier.tier)}
                disabled={!!loading || isOwned}
                style={{
                  ...styles.ctaBtn,
                  background: isOwned ? 'rgba(255,255,255,0.05)' : 'transparent',
                  border: `1px solid ${isOwned ? 'rgba(255,255,255,0.08)' : tier.color}`,
                  color: isOwned ? '#475569' : tier.color,
                  cursor: isOwned ? 'default' : loading ? 'wait' : 'pointer',
                }}
              >
                {isOwned
                  ? '✓ Owned'
                  : isBetaUser && wouldBeOwned
                    ? 'Enroll Free →'
                    : loading === tier.tier
                      ? 'Redirecting...'
                      : tier.cta}
              </button>
            </div>
          );
        })}
      </div>

      {/* Trust line */}
      <div style={styles.trust}>
        Payments processed securely by Stripe. You'll receive immediate access after checkout.
      </div>

      {/* Lifetime disclaimer */}
      <div style={{ maxWidth: 500, margin: '12px auto 0', textAlign: 'center', color: '#334155', fontSize: 12 }}>
        * "Lifetime access" means access to your purchased course content for the active life of The Operator Method platform.
      </div>

      {/* Transcript footnote */}
      <div style={{ maxWidth: 500, margin: '6px auto 0', textAlign: 'center', color: '#334155', fontSize: 12 }}>
        ** Session transcript PDFs are available as a separate purchase at $10 per session, payable at the end of any training session.
      </div>

    </div>
  );
}

const styles = {
  page: {
    position: 'relative',
    minHeight: '100vh',
    background: DESIGN_SYSTEM.backgrounds.course,
    fontFamily: "'Inter', -apple-system, sans-serif",
    color: '#e2e8f0',
    paddingTop: `${DESIGN_SYSTEM.layout.globalNavHeight + 40}px`,
    paddingLeft: '24px',
    paddingRight: '24px',
    paddingBottom: '80px',
  },
  header: {
    position: 'relative',
    zIndex: 1,
    maxWidth: 960,
    margin: '0 auto 48px',
    textAlign: 'center',
  },
  backBtn: {
    display: 'block',
    background: 'none',
    border: 'none',
    color: '#4f8ef7',
    fontSize: 14,
    cursor: 'pointer',
    marginBottom: 32,
    padding: 0,
    textAlign: 'left',
  },
  brand: {
    fontSize: 12,
    letterSpacing: '0.2em',
    textTransform: 'uppercase',
    color: '#4f8ef7',
    fontWeight: 600,
    marginBottom: 16,
  },
  headline: {
    fontSize: 42,
    fontWeight: 800,
    lineHeight: 1.1,
    marginBottom: 16,
    background: 'linear-gradient(135deg, #f0f4ff 30%, #4f8ef7)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  },
  subline: {
    color: '#64748b',
    fontSize: 17,
  },
  currentBanner: {
    maxWidth: 600,
    margin: '0 auto 32px',
    textAlign: 'center',
    background: 'rgba(52,211,153,0.08)',
    border: '1px solid rgba(52,211,153,0.25)',
    borderRadius: 10,
    padding: '12px 20px',
    fontSize: 14,
    color: '#94a3b8',
  },
  error: {
    maxWidth: 600,
    margin: '0 auto 24px',
    textAlign: 'center',
    background: 'rgba(239,68,68,0.1)',
    border: '1px solid rgba(239,68,68,0.3)',
    borderRadius: 8,
    padding: '12px 20px',
    color: '#f87171',
    fontSize: 14,
  },
  errorToast: {
    position: 'fixed',
    bottom: '28px',
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 9999,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    background: '#1f0f0f',
    border: '1px solid #dc2626',
    borderRadius: '10px',
    padding: '14px 20px',
    color: '#f87171',
    fontSize: '14px',
    fontWeight: 500,
    boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
    maxWidth: '90vw',
    whiteSpace: 'nowrap',
  },
  grid: {
    position: 'relative',
    zIndex: 1,
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: 24,
    maxWidth: 960,
    margin: '0 auto',
  },
  card: {
    position: 'relative',
    background: '#0d1220',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 16,
    padding: '32px 28px 28px',
    display: 'flex',
    flexDirection: 'column',
    transition: 'border-color 0.2s',
  },
  tooltip: {
    position: 'absolute',
    inset: 0,
    backgroundColor: 'rgba(8, 12, 28, 0.97)',
    border: '1px solid',
    borderRadius: 16,
    padding: '28px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    gap: '16px',
    zIndex: 10,
  },
  tooltipSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  tooltipLabel: {
    fontSize: 10,
    fontWeight: 800,
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
  },
  tooltipText: {
    fontSize: 14,
    color: '#94a3b8',
    lineHeight: 1.7,
    margin: 0,
  },
  tooltipDivider: {
    height: 1,
    backgroundColor: 'rgba(255,255,255,0.07)',
  },
  tierColor: {
    width: 32,
    height: 3,
    borderRadius: 2,
    marginBottom: 20,
  },
  tierName: {
    fontSize: 13,
    fontWeight: 700,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: '#64748b',
    marginBottom: 8,
  },
  tierPrice: {
    fontSize: 44,
    fontWeight: 800,
    lineHeight: 1,
    marginBottom: 4,
  },
  tierModules: {
    fontSize: 13,
    color: '#475569',
    marginBottom: 16,
  },
  tierDesc: {
    fontSize: 14,
    color: '#94a3b8',
    lineHeight: 1.6,
    marginBottom: 24,
    minHeight: 44,
  },
  featureList: {
    listStyle: 'none',
    padding: 0,
    margin: '0 0 28px',
    flex: 1,
  },
  featureItem: {
    display: 'flex',
    gap: 10,
    alignItems: 'flex-start',
    color: '#94a3b8',
    fontSize: 13,
    lineHeight: 1.5,
    marginBottom: 10,
  },
  check: {
    fontWeight: 700,
    flexShrink: 0,
    marginTop: 1,
  },
  ctaBtn: {
    padding: '13px 20px',
    borderRadius: 10,
    fontSize: 15,
    fontWeight: 600,
    textAlign: 'center',
    transition: 'opacity 0.2s',
    marginTop: 'auto',
  },
  trust: {
    maxWidth: 500,
    margin: '48px auto 0',
    textAlign: 'center',
    color: '#334155',
    fontSize: 13,
  },
};
